"use client";

import { forwardRef, useState, ComponentProps, FC } from "react";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@repo/ui/components/ui/select";
import Icon from "@repo/ui/components/ui/icon/icon";
import { cn } from "@repo/ui/lib/utils";
import { useFormField } from "@repo/ui/components/ui/form";
import { FieldError } from "react-hook-form";

interface FloatingLabelSelectProps extends ComponentProps<typeof Select> {
  id?: string;
  label: string;
  placeholder?: string;
  value?: string;
  options: string[];
  optionsLabels?: { [key: string]: string };
  // eslint-disable-next-line no-unused-vars
  onChange?: (value: string) => void;
  onBlur?: () => void;
  required?: boolean;
  error?: FieldError | boolean;
}

const FloatingLabelSelect: FC<FloatingLabelSelectProps> = forwardRef<
  HTMLButtonElement,
  FloatingLabelSelectProps
>(
  (
    {
      id,
      label,
      placeholder,
      value,
      options,
      optionsLabels,
      onChange,
      onBlur,
      required = false,
      error = false,
      ...props
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const isLabelFloating = isOpen || !!value;
    let formField;

    try {
      formField = useFormField();
    } catch (e) {
      formField = null;
    }

    const handleOpenChange = (open: boolean) => {
      setIsOpen(open);
      if (!open && onBlur) {
        onBlur();
      }
    };

    const handleValueChange = (selectedValue: string) => {
      if (onChange) {
        onChange(selectedValue);
      }
    };

    return (
      <div className="relative w-full">
        <label
          htmlFor={id}
          className={cn(
            "absolute left-[20px] top-[26px] text-sm text-secondary-black dark:text-primary-white font-black duration-150 transform origin-[0]",
            {
              "scale-75 -translate-y-3 font-medium dark:text-primary-black":
                isLabelFloating,
            },
          )}
        >
          {label} {required ? "*" : ""}
        </label>
        <Select
          required={required}
          value={value}
          onValueChange={handleValueChange}
          onOpenChange={handleOpenChange}
          {...props}
        >
          <SelectTrigger
            id={id}
            ref={ref}
            className={cn(
              "justify-start w-full h-[70px] pl-[20px] pr-[50px] pt-4 border-0 text-sm font-black text-gray-900 rounded-[10px] focus:outline-none focus:ring-1 focus:ring-secondary-black peer",
              isLabelFloating
                ? "bg-secondary-gray2"
                : "bg-secondary-gray1 dark:bg-secondary-black",
              "focus:bg-secondary-gray2 dark:focus:bg-primary-white dark:text-primary-black",
              "[&>svg]:hidden",
              { "ring-[1.5px] ring-secondary-red": formField?.error || error },
            )}
          >
            <SelectValue asChild placeholder={placeholder}>
              <span className="pointer-events-none text-left">
                {optionsLabels && value ? optionsLabels[value] : undefined}
              </span>
            </SelectValue>
            <Icon
              icon="ArrowDown"
              className={cn(
                "absolute right-[20px] top-1/2 transform -translate-y-1/2 h-[10px] w-[15px]",
                {
                  "stroke-primary-black": isOpen || !!value,
                  "stroke-primary-black dark:stroke-primary-white":
                    !isOpen && !value,
                },
              )}
            />
          </SelectTrigger>
          <SelectContent className="m-0 p-0 bg-primary-white shadow-lg rounded-[10px] overflow-hidden">
            {options.map((option) => (
              <SelectItem
                key={option}
                value={option}
                className="text-sm font-medium text-gray-900 bg-primary-white hover:bg-secondary-gray1"
              >
                {(optionsLabels && optionsLabels[option]) || option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  },
);

FloatingLabelSelect.displayName = "FloatingLabelSelect";

export { FloatingLabelSelect };
